/**
 * @file AdminPanel.js
 * @description View para o painel de administração.
 */
import { useState, useRef } from "react";
import {
  PlusCircle,
  Trash2,
  Search,
  Loader,
  Users,
  RefreshCw,
  AlertTriangle,
  UserPlus,
  Star,
  Upload,
  Shield,
} from "lucide-react";
import * as api from "../api/ligaApi";
import { useModal } from "../contexts/ModalContext";

const SectionDivider = ({ title }) => (
  <div className="flex items-center gap-3 my-1">
    <div className="flex-1 h-px bg-gray-700/60" />
    <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider flex-shrink-0">
      {title}
    </span>
    <div className="flex-1 h-px bg-gray-700/60" />
  </div>
);

const inputBase =
  "w-full bg-gray-900 border border-gray-600 text-white px-3 py-2.5 rounded-lg text-sm placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-shadow";

const AdminPanel = ({
  equipes,
  admins,
  onAddEquipe,
  onRemoveEquipe,
  onAddTime,
  onRemoveTime,
  onFetchScores,
  onUpdateAllTimes,
  onResetScores,
  onResetScoresForRound,
  onAddAdmin,
  onDeleteAdmin,
  onSetCaptain,
  onUploadLogo,
  onRefreshData,
}) => {
  const modal = useModal();
  const [newEquipeName, setNewEquipeName] = useState("");
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [rodadaAdmin, setRodadaAdmin] = useState(1);
  const [isFetchingScores, setIsFetchingScores] = useState(false);
  const [isUpdatingTeams, setIsUpdatingTeams] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isResettingRound, setIsResettingRound] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const fileInputRefs = useRef({});

  const handleLogoButtonClick = (equipeId) => {
    fileInputRefs.current[equipeId].click();
  };

  const handleFileChange = (e, equipeId) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("logo", file);
      onUploadLogo(equipeId, formData);
    }
  };

  const handleAddEquipeSubmit = (e) => {
    e.preventDefault();
    onAddEquipe(newEquipeName);
    setNewEquipeName("");
  };

  const handleUpdateTitulos = async (equipeId, novoValor) => {
    try {
      await api.updateTitulosEquipe(equipeId, novoValor);
      if (onRefreshData) onRefreshData();
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (!query) return;
    setIsSearching(true);
    setSearchError("");
    try {
      setSearchResults(await api.searchCartolaTimes(query));
    } catch (err) {
      setSearchError(err.message);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddTimeClick = async (equipeId, time) => {
    try {
      await onAddTime(equipeId, {
        nome_time: time.nome,
        nome_cartoleiro: time.nome_cartola,
        escudo_url: time.url_escudo_png,
        cartola_time_id: time.time_id,
        slug: time.slug,
      });
      setQuery("");
      setSearchResults([]);
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const handleFetchScoresClick = async () => {
    setIsFetchingScores(true);
    try {
      const result = await onFetchScores(rodadaAdmin);
      modal.showAlert({ title: "Sucesso", message: result.message, type: "success" });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    } finally {
      setIsFetchingScores(false);
    }
  };

  const handleUpdateTeamsClick = async () => {
    setIsUpdatingTeams(true);
    try {
      const result = await onUpdateAllTimes();
      modal.showAlert({ title: "Sucesso", message: result.message, type: "success" });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    } finally {
      setIsUpdatingTeams(false);
    }
  };

  const handleResetClick = async () => {
    setIsResetting(true);
    try {
      const result = await onResetScores();
      if (result) {
        modal.showAlert({ title: "Sucesso", message: result.message, type: "success" });
      }
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    } finally {
      setIsResetting(false);
    }
  };

  const handleResetRoundClick = async () => {
    setIsResettingRound(true);
    try {
      const result = await onResetScoresForRound(rodadaAdmin);
      if (result) {
        modal.showAlert({ title: "Sucesso", message: result.message, type: "success" });
      }
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    } finally {
      setIsResettingRound(false);
    }
  };

  const handleAddAdminSubmit = (e) => {
    e.preventDefault();
    onAddAdmin({ email: newAdminEmail, password: newAdminPassword });
    setNewAdminEmail("");
    setNewAdminPassword("");
  };

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-black mb-6 text-teal-400">
        Painel de Administração
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Coluna Esquerda: Equipes ── */}
        <div className="bg-gray-800 border border-gray-700/50 rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 sm:p-5 border-b border-gray-700/50">
            <h3 className="text-base font-bold text-white">Gerir Equipes e Times</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Adicione, edite ou remova equipes e seus times
            </p>
          </div>

          <div className="p-4 sm:p-5">
            {/* Formulário nova equipe */}
            <form onSubmit={handleAddEquipeSubmit} className="flex items-center gap-2 mb-5">
              <input
                type="text"
                value={newEquipeName}
                onChange={(e) => setNewEquipeName(e.target.value)}
                placeholder="Nome da Nova Equipe"
                className={`${inputBase} flex-grow`}
              />
              <button
                type="submit"
                className="flex-shrink-0 bg-teal-600 hover:bg-teal-700 text-white font-bold p-2.5 rounded-lg transition-colors"
                title="Adicionar equipe"
              >
                <PlusCircle size={18} />
              </button>
            </form>

            {/* Lista de equipes */}
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              {equipes.length === 0 && (
                <p className="text-center text-gray-500 text-sm py-6">
                  Nenhuma equipe cadastrada.
                </p>
              )}

              {equipes.map((equipe) => (
                <div
                  key={equipe.id}
                  className="bg-gray-900/60 border border-gray-700/40 rounded-xl overflow-hidden"
                >
                  {/* Cabeçalho da equipe */}
                  <div className="flex items-center justify-between px-3 py-3 border-b border-gray-700/40">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <img
                        src={api.getEscudoUrl(equipe.logo_url) || "/logo-equipe-padrao.png"}
                        alt="Logo"
                        className="w-9 h-9 rounded-full object-contain flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <h4 className="font-bold text-teal-300 text-sm leading-tight truncate">
                          {equipe.nome}
                        </h4>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Star size={11} className="text-yellow-500 fill-yellow-500 flex-shrink-0" />
                          <input
                            type="number"
                            min="0"
                            className="w-10 bg-gray-800 text-white text-xs p-1 rounded border border-gray-600 focus:border-teal-500 outline-none text-center"
                            defaultValue={equipe.titulos || 0}
                            onBlur={(e) => handleUpdateTitulos(equipe.id, e.target.value)}
                            title="Quantidade de títulos"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <input
                        type="file"
                        ref={(el) => (fileInputRefs.current[equipe.id] = el)}
                        className="hidden"
                        onChange={(e) => handleFileChange(e, equipe.id)}
                        accept="image/png, image/jpeg"
                      />
                      <button
                        onClick={() => handleLogoButtonClick(equipe.id)}
                        className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                        title="Fazer upload do logo"
                      >
                        <Upload size={15} />
                      </button>
                      <button
                        onClick={() => onRemoveEquipe(equipe.id)}
                        className="p-2 rounded-lg text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        title="Remover equipe"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  {/* Times da equipe */}
                  <div className="px-3 pt-2 pb-2">
                    {equipe.times.length === 0 && (
                      <p className="text-gray-500 italic text-xs py-2 text-center">
                        Nenhum time adicionado.
                      </p>
                    )}
                    <div className="space-y-1.5 mb-2">
                      {equipe.times
                        .sort((a, b) => a.nome_time.localeCompare(b.nome_time))
                        .map((time) => (
                          <div
                            key={time.id}
                            className="bg-gray-700/40 px-2 py-2 rounded-lg flex items-center justify-between"
                          >
                            <div className="flex items-center gap-1.5 min-w-0">
                              <button
                                onClick={() => onSetCaptain(time.id, time.equipe_id)}
                                className="p-1 rounded-full hover:bg-gray-600 transition-colors flex-shrink-0"
                                title="Definir como capitão"
                              >
                                <Star
                                  size={14}
                                  className={
                                    time.is_captain
                                      ? "text-yellow-400 fill-yellow-400"
                                      : "text-gray-600"
                                  }
                                />
                              </button>
                              <img
                                src={api.getEscudoUrl(time.escudo_url)}
                                alt={`Escudo do ${time.nome_time}`}
                                className="w-7 h-7 rounded-full flex-shrink-0"
                              />
                              <div className="min-w-0">
                                <p className="font-semibold text-sm text-white truncate">
                                  {time.nome_time}
                                </p>
                                <p className="text-xs text-gray-400 truncate">
                                  {time.nome_cartoleiro}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => onRemoveTime(time.id)}
                              className="p-1.5 rounded-lg text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0 ml-1"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                    </div>

                    {/* Busca de times do Cartola */}
                    {equipe.times.length < 7 && (
                      <div className="bg-gray-800/60 border border-gray-700/40 rounded-lg p-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                          Adicionar Time do Cartola
                        </p>
                        <form
                          onSubmit={handleSearchSubmit}
                          className="flex items-center gap-2 mb-2"
                        >
                          <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Buscar time..."
                            className="flex-grow bg-gray-900 border border-gray-700 text-white p-2 rounded-lg text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-teal-500"
                          />
                          <button
                            type="submit"
                            className="bg-blue-600 hover:bg-blue-700 p-2 rounded-lg transition-colors flex-shrink-0"
                            disabled={isSearching}
                          >
                            {isSearching ? (
                              <Loader size={16} className="animate-spin" />
                            ) : (
                              <Search size={16} />
                            )}
                          </button>
                        </form>
                        {searchError && (
                          <p className="text-red-400 text-xs mb-2">{searchError}</p>
                        )}
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                          {searchResults.map((time) => (
                            <div
                              key={time.time_id}
                              className="bg-gray-900/60 p-2 rounded-lg flex items-center justify-between"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <img
                                  src={api.getEscudoUrl(time.url_escudo_png)}
                                  alt={`Escudo do ${time.nome}`}
                                  className="w-7 h-7 rounded-full flex-shrink-0"
                                />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-white truncate">
                                    {time.nome}
                                  </p>
                                  <p className="text-xs text-gray-400 truncate">
                                    {time.nome_cartola}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleAddTimeClick(equipe.id, time)}
                                className="bg-green-600 hover:bg-green-700 p-1.5 rounded-lg text-white transition-colors flex-shrink-0 ml-1"
                              >
                                <PlusCircle size={15} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Coluna Direita: Ações + Admins ── */}
        <div className="flex flex-col gap-5">
          {/* Ações da Liga */}
          <div className="bg-gray-800 border border-gray-700/50 rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-gray-700/50">
              <h3 className="text-base font-bold text-white">Ações da Liga</h3>
            </div>
            <div className="p-4 sm:p-5 space-y-5">
              {/* Sincronizar Pontuações */}
              <div>
                <p className="text-sm font-semibold text-gray-300 mb-3">
                  Sincronizar Pontuações
                </p>
                <div className="flex items-center gap-3 mb-3">
                  <label htmlFor="rodada-select-admin" className="text-sm text-gray-400 flex-shrink-0">
                    Rodada:
                  </label>
                  <select
                    id="rodada-select-admin"
                    value={rodadaAdmin}
                    onChange={(e) => setRodadaAdmin(Number(e.target.value))}
                    className="bg-gray-700 border border-gray-600 text-white p-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {[...Array(38).keys()].map((i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    onClick={handleFetchScoresClick}
                    className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-2.5 px-3 rounded-lg transition-colors text-sm"
                    disabled={isFetchingScores}
                  >
                    {isFetchingScores ? (
                      <Loader size={15} className="animate-spin" />
                    ) : (
                      <RefreshCw size={15} />
                    )}
                    {isFetchingScores ? "Buscando..." : "Buscar Pontos"}
                  </button>
                  <button
                    onClick={handleResetRoundClick}
                    className="flex items-center justify-center gap-2 bg-red-800 hover:bg-red-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-2.5 px-3 rounded-lg transition-colors text-sm"
                    disabled={isResettingRound}
                  >
                    {isResettingRound ? (
                      <Loader size={15} className="animate-spin" />
                    ) : (
                      <Trash2 size={15} />
                    )}
                    {isResettingRound ? "Apagando..." : "Resetar Rodada"}
                  </button>
                </div>
              </div>

              <SectionDivider title="dados" />

              {/* Atualizar Times */}
              <div>
                <p className="text-sm font-semibold text-gray-300 mb-1">
                  Atualizar Dados dos Times
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Busca os nomes e escudos mais recentes de todos os times da liga.
                </p>
                <button
                  onClick={handleUpdateTeamsClick}
                  className="w-full flex items-center justify-center gap-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
                  disabled={isUpdatingTeams}
                >
                  {isUpdatingTeams ? (
                    <Loader size={15} className="animate-spin" />
                  ) : (
                    <Users size={15} />
                  )}
                  {isUpdatingTeams ? "Atualizando..." : "Atualizar Times"}
                </button>
              </div>

              <SectionDivider title="zona de perigo" />

              {/* Resetar Tudo */}
              <div>
                <p className="text-sm font-semibold text-red-400 mb-1">
                  Resetar Todas as Pontuações
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Apaga permanentemente todas as pontuações. Esta ação não pode ser desfeita.
                </p>
                <button
                  onClick={handleResetClick}
                  className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
                  disabled={isResetting}
                >
                  {isResetting ? (
                    <Loader size={15} className="animate-spin" />
                  ) : (
                    <AlertTriangle size={15} />
                  )}
                  {isResetting ? "Resetando..." : "Resetar Tudo"}
                </button>
              </div>
            </div>
          </div>

          {/* Gerir Administradores */}
          <div className="bg-gray-800 border border-gray-700/50 rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-gray-700/50">
              <h3 className="text-base font-bold text-white">Gerir Administradores</h3>
            </div>
            <div className="p-4 sm:p-5">
              <form onSubmit={handleAddAdminSubmit} className="space-y-3 mb-5">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Adicionar Novo Admin
                </p>
                <input
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  placeholder="Email do novo admin"
                  required
                  className={inputBase}
                />
                <input
                  type="password"
                  value={newAdminPassword}
                  onChange={(e) => setNewAdminPassword(e.target.value)}
                  placeholder="Senha (mín. 6 caracteres)"
                  required
                  minLength="6"
                  className={inputBase}
                />
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
                >
                  <UserPlus size={15} />
                  Adicionar Admin
                </button>
              </form>

              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Admins Atuais
                </p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {admins.length === 0 && (
                    <p className="text-center text-gray-500 text-xs py-3">
                      Nenhum admin cadastrado.
                    </p>
                  )}
                  {admins.map((admin) => (
                    <div
                      key={admin.id}
                      className="bg-gray-700/40 px-3 py-2 rounded-lg flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Shield size={13} className="text-gray-500 flex-shrink-0" />
                        <p className="text-sm text-gray-300 truncate">{admin.email}</p>
                      </div>
                      <button
                        onClick={() => onDeleteAdmin(admin.id)}
                        className="p-1.5 rounded-lg text-red-500 hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
